import {
  Avatar,
  Box,
  Card,
  CardContent,
  Link,
  Stack,
  Typography,
  TypographyProps,
} from '@mui/material'
import sortBy from 'lodash/sortBy'
import React, { FC, useCallback, useMemo, useRef, useState } from 'react'
import { Swiper, SwiperClass, SwiperRef, SwiperSlide } from 'swiper/react'
import { IconButton, Icons } from 'utoniq-ui'

import {
  ItemSerial,
  Maybe,
  PortfolioConnectionFragment,
  TicketItemMetadataAssignedNumberType,
  TicketStatusThroughTicket,
  UserItemSerialDetailFragment,
} from '@/clients/utoniq-core/schema'
import { State } from '@/components/pages/OwnedItemDetail/viewModel'
import { useRouter, useTranslation } from '@/core'
import { Organism, OrganismFrame } from '@/core/architecture'

type TicketNumberBoxProps = {
  loggedUser: State['loggedUser']
  item?: PortfolioConnectionFragment['item']
  serials?: UserItemSerialDetailFragment[]
}

export const TicketNumberBox: Organism<TicketNumberBoxProps> = ({
  loggedUser,
  item,
  serials,
}) => {
  const swiperRef = useRef<SwiperRef>(null)
  const { t } = useTranslation('ownedItemDetail')
  const [throughTicketIndex, setThroughTicketIndex] = useState<number>(0)

  const isThroughTicket = !!item?.metadata?.ticket?.isThroughTicket
  const throughTickets = item?.metadata?.ticket?.throughTicket ?? [
    { name: null },
  ]

  const showMessWaitAssignSeat = useMemo(
    () =>
      checkShowMessWaitAssignSeat(
        item?.metadata?.ticket?.assignedNumberType,
        serials,
        throughTicketIndex
      ),
    [item?.metadata?.ticket?.assignedNumberType, serials, throughTicketIndex]
  )

  if (!serials?.length || showMessWaitAssignSeat) {
    return null
  }

  return (
    <OrganismFrame sx={{ width: '100%', px: 5 }}>
      <Card elevation={0} sx={{ width: '100%', borderRadius: '6px' }}>
        <CardContent sx={{ px: 4, py: 3, '&:last-child': { pb: 3 } }}>
          {!!throughTickets?.length && isThroughTicket && (
            <Stack direction="row" alignItems="center" pb={2}>
              <IconButton.Prev
                onClick={() => {
                  swiperRef?.current?.swiper.enable()
                  swiperRef?.current?.swiper.slidePrev()
                }}
                bgcolor="grey.100"
                sx={{
                  bgcolor: 'grey.100',
                  visibility: throughTicketIndex === 0 ? 'hidden' : 'visible',
                }}
              />
              <Stack flex={1}>
                <Typography variant="subtitle1" width="100%" align="center">
                  {throughTickets[throughTicketIndex]?.name}
                </Typography>
              </Stack>
              <IconButton.Next
                bgcolor="grey.100"
                onClick={() => {
                  swiperRef?.current?.swiper.enable()
                  swiperRef?.current?.swiper.slideNext()
                }}
                sx={{
                  bgcolor: 'grey.100',
                  visibility:
                    throughTicketIndex + 1 === throughTickets.length
                      ? 'hidden'
                      : 'visible',
                }}
              />
            </Stack>
          )}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            py="6px"
          >
            <TicketLabel
              serial={serials?.[0]?.serial}
              assignedNumberType={item?.metadata?.ticket?.assignedNumberType}
            />
            {item?.isTicketDistributionEnabled && (
              <Typography variant="caption">
                {t('distribute', '分配状況')}
              </Typography>
            )}
          </Stack>
          <Swiper
            ref={swiperRef}
            loop={false}
            spaceBetween={20}
            allowTouchMove={false}
            enabled={throughTickets?.length > 1}
            onActiveIndexChange={(swiper: SwiperClass) =>
              setThroughTicketIndex(swiper.activeIndex)
            }
          >
            {throughTickets?.map((_, index) => (
              <SwiperSlide key={index}>
                <TicketContainer
                  serials={serials}
                  loggedUser={loggedUser}
                  isThroughTicket={isThroughTicket}
                  throughTicketIndex={index}
                  isTicketDistributionEnabled={
                    !!item?.isTicketDistributionEnabled
                  }
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </CardContent>
      </Card>
    </OrganismFrame>
  )
}

type TicketLabelProps = {
  assignedNumberType?: Maybe<TicketItemMetadataAssignedNumberType>
  serial?: Maybe<Pick<ItemSerial, 'formattedAssignedNumber'>>
  typographyProps?: Pick<TypographyProps, 'variant' | 'color'>
}

export const TicketLabel: FC<TicketLabelProps> = ({
  assignedNumberType,
  serial,
  typographyProps,
}) => {
  const { t } = useTranslation('ownedItemDetail')

  const ticketLabel = useMemo<string>(() => {
    if (assignedNumberType === TicketItemMetadataAssignedNumberType.Seat) {
      return t('seatNumberLabel', '座席番号')
    }
    if (serial?.formattedAssignedNumber) {
      return t('referenceNumberLabel', '整理番号')
    }
    return t('serialNumberLabel', 'シリアルナンバー')
  }, [assignedNumberType, serial?.formattedAssignedNumber, t])

  return (
    <Typography component="p" variant="caption" {...typographyProps}>
      {ticketLabel}
    </Typography>
  )
}

type TicketContainerProps = {
  loggedUser: State['loggedUser']
  isThroughTicket: boolean
  throughTicketIndex: number
  serials: UserItemSerialDetailFragment[]
  isTicketDistributionEnabled: boolean
}
const TicketContainer: FC<TicketContainerProps> = ({
  loggedUser,
  serials,
  throughTicketIndex,
  isThroughTicket,
  isTicketDistributionEnabled,
}) => {
  const { t } = useTranslation('ownedItemDetail')
  const { router, routerPages, generateQueryPath } = useRouter<{
    itemId: string
    skuId?: string
  }>()
  const getTicket = useCallback(
    (serial: UserItemSerialDetailFragment) => {
      const label = getSerialFormatNumber(
        serial.serial,
        isThroughTicket ? throughTicketIndex : undefined
      )
      if (isThroughTicket) {
        return {
          label,
          isUsed:
            serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
              .isUsed,
        }
      }
      return {
        label,
        isUsed: serial.serial?.ticketStatus?.isUsed,
      }
    },
    [isThroughTicket, throughTicketIndex]
  )

  const getTicketStatusNode = useCallback(
    (serial: UserItemSerialDetailFragment, isUsed: boolean) => {
      const disabledLink =
        loggedUser?.uid === serial?.serial?.ticketDistributionLink?.user?.id ||
        isUsed ||
        !isTicketDistributionEnabled
      const isOwnerTicket = loggedUser?.uid === serial?.serial?.owner?.id

      if (serial.serial?.ticketDistributionLink?.user) {
        return {
          href: generateQueryPath(
            routerPages.TicketShareCopy,
            {
              itemId: router.query.itemId,
              serialId: serial.serialId,
            },
            {
              throughTicketIndex: isThroughTicket
                ? throughTicketIndex.toString()
                : '',
            }
          ),
          disabledLink:
            loggedUser?.uid ===
              serial?.serial?.ticketDistributionLink?.user?.id || isUsed,
          ticketStatusNode: (
            <Stack direction="row" spacing={1} alignItems="center">
              <Avatar
                src={
                  (isOwnerTicket
                    ? serial.serial.ticketDistributionLink.user.avatar?.smallUri
                    : serial.serial?.owner?.avatar?.smallUri) ?? ''
                }
                sx={{ width: 16, height: 16 }}
              />
              <Typography variant="overline" color="text.secondary">
                {isOwnerTicket
                  ? t('distributedToUser', {
                      name: serial.serial.ticketDistributionLink.user
                        .displayName,
                    })
                  : t('receivedFromUser', {
                      name: serial.serial.owner?.displayName,
                    })}
              </Typography>
            </Stack>
          ),
        }
      }
      if (
        serial.serial?.ticketDistributionLink?.isExpired &&
        isTicketDistributionEnabled &&
        !isUsed
      ) {
        return {
          href: generateQueryPath(
            routerPages.TicketShareSelect,
            {
              itemId: router.query.itemId,
              serialId: serial.serialId,
            },
            {
              throughTicketIndex: isThroughTicket
                ? throughTicketIndex.toString()
                : '',
            }
          ),
          disabledLink,
          ticketStatusNode: (
            <Stack direction="row" spacing={1} alignItems="center">
              <Icons.Info color="error" sx={{ width: 16, height: 16 }} />
              <Typography variant="overline" color="error.main">
                {t('distributionURLExpired', '分配URL期限切れ')}
              </Typography>
            </Stack>
          ),
        }
      }
      if (
        serial.serial?.ticketDistributionLink &&
        isTicketDistributionEnabled &&
        !isUsed
      ) {
        return {
          href: generateQueryPath(
            routerPages.TicketShareCopy,
            {
              itemId: router.query.itemId,
              serialId: serial.serialId,
            },
            {
              throughTicketIndex: isThroughTicket
                ? throughTicketIndex.toString()
                : '',
            }
          ),
          disabledLink,
          ticketStatusNode: (
            <Typography variant="overline" color="text.secondary">
              {t('distribution', '分配中')}
            </Typography>
          ),
        }
      }

      if (isTicketDistributionEnabled) {
        return {
          href: generateQueryPath(
            routerPages.TicketShareSelect,
            {
              itemId: router.query.itemId,
              serialId: serial.serialId,
            },
            {
              throughTicketIndex: isThroughTicket
                ? throughTicketIndex.toString()
                : '',
            }
          ),
          disabledLink,
          ticketStatusNode: null,
        }
      }
      return { href: null, disabledLink, ticketStatusNode: null }
    },
    [
      loggedUser?.uid,
      isTicketDistributionEnabled,
      generateQueryPath,
      routerPages.TicketShareCopy,
      routerPages.TicketShareSelect,
      router.query.itemId,
      isThroughTicket,
      throughTicketIndex,
      t,
    ]
  )

  return (
    <Box>
      <Stack spacing={2}>
        {sortBy(serials, serial => serial.formattedSerialNumber).map(serial => {
          const { label, isUsed } = getTicket(serial)
          const { href, disabledLink, ticketStatusNode } = getTicketStatusNode(
            serial,
            !!isUsed
          )
          return (
            <Stack
              direction="row"
              spacing={2}
              alignItems="center"
              justifyContent="space-between"
              py="2px"
              key={serial.id}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {isUsed && (
                  <Icons.Check
                    color="primary"
                    sx={{
                      width: 16,
                      height: 16,
                    }}
                  />
                )}
                <Typography
                  variant="subtitle1"
                  component={!disabledLink ? Link : 'p'}
                  href={!disabledLink && href ? href : undefined}
                  color={!disabledLink ? 'primary.main' : 'text.primary'}
                  sx={{ whiteSpace: 'nowrap' }}
                >
                  {label}
                </Typography>
              </Stack>
              {ticketStatusNode}
            </Stack>
          )
        })}
      </Stack>
    </Box>
  )
}

export function checkShowMessWaitAssignSeat(
  assignedNumberType: Maybe<TicketItemMetadataAssignedNumberType> | undefined,
  serials: UserItemSerialDetailFragment[] | undefined,
  throughTicketIndex: number
): boolean {
  if (
    assignedNumberType !== TicketItemMetadataAssignedNumberType.Seat ||
    !serials?.length
  ) {
    return false
  }
  const isThroughTicket = serials?.some(
    serial => serial.serial?.ticketStatus?.throughTicket?.length
  )
  if (isThroughTicket) {
    return serials?.some(
      serial =>
        serial.serial?.ticketStatus?.throughTicket?.[throughTicketIndex]
          ?.seat === null
    )
  }
  return serials?.some(
    serial => serial.serial?.formattedAssignedNumber === null
  )
}

type SerialType = Pick<
  ItemSerial,
  'formattedAssignedNumber' | 'formattedSerialNumber'
> & {
  ticketStatus?: Maybe<{
    throughTicket?: Maybe<Array<Pick<TicketStatusThroughTicket, 'seat'>>>
  }>
}
export function getSerialFormatNumber(
  serial?: Maybe<SerialType>,
  throughTicketIndex?: number | string
) {
  if (throughTicketIndex !== undefined) {
    return (
      serial?.ticketStatus?.throughTicket?.[+throughTicketIndex]?.seat ??
      serial?.formattedAssignedNumber ??
      serial?.formattedSerialNumber ??
      ''
    )
  }
  return serial?.formattedAssignedNumber ?? serial?.formattedSerialNumber ?? ''
}
